import moment from "moment";

import { SearchQuery } from "shared/data/reducers/types/storage";
import {
  getJourneyDurationInMinutes,
  getTimeDifference,
  rangeNumToMinutes,
  rangeNumToTime,
} from "shared/lib/functions";
import FlightOffer from "shared/types/flightOffer";
import FlightStream from "shared/types/flightStream";

const filterFlightTimesMinMax = (key: string, searchQuery: SearchQuery) => {
  const minKey = key.includes("min") ? key : key.replace("max", "min");
  const minValue = Number(searchQuery[minKey]);

  const maxKey = key.includes("max") ? key : key.replace("min", "max");
  const maxValue = Number(searchQuery[maxKey]);

  return {
    min: minValue,
    max: maxValue,
  };
};

const arrivalTimeFilterFn = (key: string, searchQuery: SearchQuery) => (
  currentFlightStream: FlightStream
): boolean => {
  if (!key.includes("arrival")) return true;

  const time = moment(
    currentFlightStream.ArrivalDateTime,
    moment.HTML5_FMT.DATETIME_LOCAL
  ).format("HH:mm");

  const minMax = filterFlightTimesMinMax(key, searchQuery);
  return (
    time >= rangeNumToTime(minMax.min) && time <= rangeNumToTime(minMax.max)
  );
};

const departureTimeFilterFn = (key: string, searchQuery: SearchQuery) => (
  currentFlightStream: FlightStream
): boolean => {
  if (!key.includes("departure")) return true;

  const time = moment(
    currentFlightStream.DepartureDateTime,
    moment.HTML5_FMT.DATETIME_LOCAL
  ).format("HH:mm");

  const minMax = filterFlightTimesMinMax(key, searchQuery);
  return (
    time >= rangeNumToTime(minMax.min) && time <= rangeNumToTime(minMax.max)
  );
};

const transferTimeFilterFn = (key: string, searchQuery: SearchQuery) => (
  currentFlightStream: FlightStream
): boolean => {
  if (!key.includes("transfer")) return true;

  const transferTime = { hours: 0, minutes: 0 };

  let lastSegment = null;

  currentFlightStream.FlightSegments.FlightSegment.forEach((currentSegment) => {
    if (lastSegment === null) {
      lastSegment = currentSegment;
    } else {
      const timeDiff = getTimeDifference(
        lastSegment.ArrivalDateTime,
        currentSegment.DepartureDateTime
      );
      transferTime.hours += timeDiff.hours;
      transferTime.minutes += timeDiff.minutes;
    }
  });

  transferTime.hours += Math.floor(transferTime.minutes / 60);
  transferTime.minutes %= 60;

  const time =
    transferTime.hours + Number(((1 / 60) * transferTime.minutes).toFixed(1));

  const minMax = filterFlightTimesMinMax(key, searchQuery);

  return time >= minMax.min && time <= minMax.max;
};

const jurneyDurationFilterFn = (key: string, searchQuery: SearchQuery) => (
  currentFlightStream: FlightStream
): boolean => {
  if (!key.includes("journeyDuration")) return true;

  const time = getJourneyDurationInMinutes(currentFlightStream.JourneyDuration);

  const minMax = filterFlightTimesMinMax(key, searchQuery);

  return (
    time >= rangeNumToMinutes(minMax.min) &&
    time <= rangeNumToMinutes(minMax.max)
  );
};

const filterFlightTimes = (searchQuery: SearchQuery) => (
  flightOffer: FlightOffer
) => {
  let ret = true;

  const paramKeys = Object.keys(searchQuery).filter((param) =>
    param.includes("filter_")
  );

  paramKeys.forEach((key) => {
    // eslint-disable-next-line no-magic-numbers
    const flightStreamIndex = Number(key.charAt(7));
    const flightStreams = flightOffer.FlightItinerary.FlightStream;

    const allowedFlights = flightStreams[flightStreamIndex]
      .filter(arrivalTimeFilterFn(key, searchQuery))
      .filter(departureTimeFilterFn(key, searchQuery))
      .filter(transferTimeFilterFn(key, searchQuery))
      .filter(jurneyDurationFilterFn(key, searchQuery));

    if (allowedFlights.length !== 0) {
      // eslint-disable-next-line no-param-reassign
      flightOffer.FlightItinerary.FlightStream[
        flightStreamIndex
      ] = allowedFlights;
    } else {
      ret = false;
    }
  });

  return ret;
};

export default filterFlightTimes;
